import { Product_2022_06 } from "@rechargeapps/storefront-client";
import { useState } from "react";
import { Button } from "./Button";
import { Loader2 } from "lucide-react";
import cn from "clsx";
import { useGlobal } from "../context/globalProvider";
import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription, DialogClose } from "./Dialog";
import QuickviewDialog from "./QuickviewDialog";
import SubscriptionAddDialog from "./SubscriptionAddDialog";

const ProductItem = ({ 
  product, 
  isCurrentProduct,  
  onSubmit,
  source = 'search',
  setSelectedProduct,
  swapFromProductId,
  subscriptionOnly,
  sale,
  properties,
}: { 
  product: Product_2022_06, 
  subscriptionOnly: boolean,
  swapFromProductId: string,
  isCurrentProduct: boolean, 
  onSubmit: (product: Product_2022_06) => Promise<void>,
  setSelectedProduct: (product: Product_2022_06) => void,
  sale?: boolean,
  properties?: any[],
  subscriptionOnly?: boolean,
  source: string,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { orders } = useGlobal();
  const nextOrder = orders?.upcoming?.[0];

  let price = product.selectedVariant?.original_price || product.selectedVariant?.price || product.price;
  let compareAtPrice = product.selectedVariant?.compare_at_price || product.compare_at_price;
  let subscriptionPrice = product.selectedVariant?.original_subscription_price || product.selectedVariant?.subscription_price || product.subscription_price;
  let subscriptionCompareAtPrice = product.selectedVariant?.subscription_compare_at_price || 0;

  if (sale) {
    price = product.selectedVariant?.price || product.price;
    compareAtPrice = product.selectedVariant?.compare_at_price || product.compare_at_price;
    subscriptionPrice = product.selectedVariant?.subscription_price || product.subscription_price;
    subscriptionCompareAtPrice = product.selectedVariant?.subscription_compare_at_price || 0;
  }

  let subscriptionButtonText = subscriptionPrice ? `Add subscription - ${subscriptionPrice}/MO.` : "";
  let onetimeButtonText = price ? `Add one time - ${price}` : "";

  if (sale) {
    subscriptionButtonText = `Subscribe - ${subscriptionPrice}/MO.`;
    onetimeButtonText = `One time - ${price}`;
  }

  let buttonText = `Confirm swap - ${subscriptionPrice}/MO.`;
  let buttonTextMobile = `Swap - ${price}`;
  if (isCurrentProduct) buttonText = 'Current';

  const handleSubmit = async (hasPlans: boolean) => {
    try {
      setIsLoading(true);
      await onSubmit({
        ...product, 
        has_plans: hasPlans, 
        plans: hasPlans ? product.first_selling_plan_allocation : []
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <article className={cn("flex flex-col items-center gap-4 h-full", {
      "p-2 border": isCurrentProduct,
    })}>
      <div className="relative flex w-full aspect-square">
        <QuickviewDialog
          isSale={sale}
          product={product}
          source={source}
          // isSale={sale}
          subscriptionOnly={subscriptionOnly}
          Trigger={
            <button type="button" className="">
              <img 
                src={product.selectedVariant?.image || product.image} 
                alt={product.title} 
                className="w-full aspect-square object-cover" 
                loading="lazy"
              />
              <div className="hidden md:flex product-quickview group/quickview absolute bottom-2 right-2 sm:bottom-3 sm:right-3 bg-white px-[6px] sm:px-[13px] h-[25px] w-[25px] sm:h-[40px] sm:w-[40px] sm:hover:w-[120px] md:hover:w-[130px] transition-all duration-300 rounded-full items-center overflow-hidden">
                <span className="uppercase text-xs font-semibold tracking-wider flex items-center gap-2 shrink-0">              
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.19375 6.92019H6.98901V9.14583H6.05905V6.92019H3.86477V6.05293H6.05905V3.81685H6.98901V6.05293H9.19375V6.92019Z" fill="#333333"/>
                    <path d="M6.55583 0C10.1773 0 13.1125 2.89606 13.1125 6.46734C13.1136 8.01721 12.5515 9.5147 11.5309 10.681L15.8412 15.0595C15.8926 15.1114 15.9331 15.173 15.9603 15.2408C15.9876 15.3085 16.0011 15.381 15.9999 15.4541C15.9988 15.5271 15.9831 15.5991 15.9538 15.666C15.9245 15.7329 15.8821 15.7932 15.8292 15.8435C15.7216 15.9459 15.5782 16.002 15.4297 15.9999C15.2812 15.9978 15.1395 15.9377 15.0348 15.8323L10.7285 11.4562C9.55006 12.4162 8.07577 12.9389 6.55583 12.9355C2.93592 12.9355 0 10.0394 0 6.46734C0 2.89606 2.93592 0 6.55583 0ZM6.55583 1.10882C3.55591 1.10882 1.12397 3.50808 1.12397 6.46734C1.12397 9.4274 3.55591 11.8267 6.55583 11.8267C9.55575 11.8267 11.9885 9.4266 11.9885 6.46734C11.9885 3.50728 9.55655 1.10882 6.55663 1.10882H6.55583Z" fill="#333333"/>
                  </svg>
                  <span className="opacity-0 group-hover/quickview:opacity-100 transition-opacity duration-300">QUICK VIEW</span>
                </span>
              </div>
            </button>
          } 
        />
      </div>

      <div className="flex flex-col items-start w-full flex-1">
        <QuickviewDialog
          isSale={sale}
          product={product}
          source={source}
          // isSale={sale}
          subscriptionOnly={subscriptionOnly}
          Trigger={
            <button type="button" className="text-left w-full">
              <h3 className="text-lg leading-tight mb-1">{product.title}</h3>
              <p className="text-xs uppercase tracking-wider font-semibold">{product?.short_description?.replaceAll('&amp;', '&')}</p>
            </button>
          } 
        />

        <div className="mt-auto pt-4 w-full flex flex-col gap-2">
          {swapFromProductId ? (
            <Button 
              className="w-full" 
              size="sm"
              variant="default"
              disabled={isLoading || isCurrentProduct}
              onClick={async () => {
                try {
                  setIsLoading(true);
                  await onSubmit(product);
                } catch (error) {
                  console.error(error);
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              <span className={cn({ "opacity-0": isLoading })}>
                <span className="hidden md:inline">{buttonText}</span>
                <span className="inline md:hidden">{buttonTextMobile || buttonText}</span>
              </span>
              {isLoading && <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"><Loader2 className="w-4 h-4 animate-spin" /></span>}
            </Button>
          ) : (
            <>
              <SubscriptionAddDialog 
                source={source}
                product={product} 
                nextOrder={nextOrder}
                properties={properties}
                firstVariant={product.selectedVariant}
                Trigger={<Button 
                  className="w-full" 
                  size="sm"
                  variant="default"
                  disabled={isLoading || isCurrentProduct || !subscriptionPrice || (product.selectedVariant && !product.selectedVariant.selling_plan_id)}
                >
                  {
                    (product.selectedVariant && !product.selectedVariant?.selling_plan_id) ? (
                      "Subscription not available"
                    ) : (
                      sale ? (
                        <>
                          <span>{subscriptionButtonText}</span>
                          {
                            !subscriptionButtonText.includes(subscriptionCompareAtPrice) ? (
                              <del className="line-through ml-0.5 opacity-50 hidden md:inline">{subscriptionCompareAtPrice}/MO</del>
                            ) : null
                          }
                          
                        </>
                      ) : (
                        subscriptionButtonText
                      )
                    )
                  }
                  
                </Button>}
              />

              {
                !subscriptionOnly && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        className="w-full" 
                        size="sm"
                        variant="outline"
                        disabled={isLoading || isCurrentProduct || !product?.price}
                      >
                        {sale ? (
                          <>
                            <span>{onetimeButtonText}</span>
                            <del className="line-through ml-0.5 opacity-50">{compareAtPrice}</del>
                          </>
                        ) : (
                          onetimeButtonText
                        )}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white">
                      <DialogTitle>Add One-Time Purchase</DialogTitle>
                      <DialogDescription>Are you sure you want to add this one-time purchase to your cart?</DialogDescription>
                      <div className="flex justify-end gap-2">
                        <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                        <Button 
                          disabled={isLoading}
                          onClick={() => handleSubmit(false)}
                        >
                          <span className={cn({ "opacity-0": isLoading })}>Confirm</span>
                          {isLoading && <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"><Loader2 className="w-4 h-4 animate-spin" /></span>}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )
              }
            </>
          )}
        </div>
      </div>
    </article>
  )
}

export default ProductItem;